import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ContactInformationFields } from "src/core/components/create-client/ContactInformationFields";
import { FollowButtonField } from "src/core/components/create-client/FollowButtonField";
import {
  FormUserSearch,
  FormUserSearchMulti,
} from "src/core/components/custom-ui/form-user-search";
import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { Form, FormLabel } from "src/core/components/ui/form";
import { ISelectOption } from "src/core/constants/options";
import { Roles } from "src/core/constants/roles";
import { useFolder } from "src/core/contexts/FolderContext";
import { useContactFormController } from "src/core/hooks/contact/useContactFormController";
import { usePermissions } from "src/core/hooks/usePermissions";
import { Contact, Owner } from "src/core/types/contact";

type EditContactModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  customerFolderId: string;
  contactPerson: Contact | null;
  owner: Owner | null;
  onSuccess: () => void;
};

export const EditContactModal = ({
  open,
  setOpen,
  customerFolderId,
  contactPerson,
  owner,
  onSuccess,
}: EditContactModalProps) => {
  const { t } = useTranslation();

  const { form, isLoading, onSubmit } = useContactFormController(
    customerFolderId,
    setOpen,
    contactPerson,
    owner,
    onSuccess
  );
  const { folderFile } = useFolder();
  const { hasRole } = usePermissions();
  const pinnedExpert = useMemo<ISelectOption[]>(() => {
    if (!owner?.id) return [];
    const label = `${owner.firstName ?? ""} ${owner.lastName ?? ""}`.trim();
    return label ? [{ value: owner.id, label }] : [];
  }, [owner]);
  const pinnedFollowers = useMemo<ISelectOption[]>(
    () =>
      folderFile.folderFile?.currentCustomerFolder?.followingUsers?.flatMap((user) => {
        const label = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
        return user.id && label ? [{ value: user.id, label }] : [];
      }) ?? [],
    [folderFile]
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="edit-contact-modal"
      >
        <DialogHeader className="modal-edit-contact-header">
          <DialogTitle>{t("editContact.modal.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("editContact.modal.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="modal-edit-contact-description">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-2 items-start gap-x-30 mb-10">
                <div className="col-span-1">
                  <FormUserSearch
                    control={form.control}
                    name="expertComptable"
                    label={t("form.editContact.inCharge")}
                    pinnedOptions={pinnedExpert}
                    className="w-[300px]"
                    clearable
                    isInModal
                  />
                </div>
                {hasRole(Roles.COLLABORATEUR) ? (
                  <div className="col-span-1 flex flex-col h-full justify-between">
                    <FormLabel>{t("form.createClient.followLabel")}</FormLabel>
                    <FollowButtonField
                      isFollowing={form.watch("isFollowing") || false}
                      onClick={(event) => {
                        event.preventDefault();
                        form.setValue("isFollowing", !form.watch("isFollowing"));
                      }}
                    />
                  </div>
                ) : (
                  <FormUserSearchMulti
                    control={form.control}
                    name="folowers"
                    className="min-w-[310px]"
                    label={t("form.createClient.toFollowLabel")}
                    placeholder={t("form.createClient.toFollowPlaceholder")}
                    pinnedOptions={pinnedFollowers}
                    isInModal
                  />
                )}
              </div>

              <ContactInformationFields form={form} />

              <DialogFooter>
                <Button type="submit" variant="default" className="ml-[20px]" loading={isLoading}>
                  {t("form.editContact.submit")}
                </Button>
                <Button
                  variant="link"
                  onClick={(event) => {
                    event.preventDefault();
                    setOpen(false);
                  }}
                >
                  {t("global.form.cancel")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
